// Library entry: re-exports board components for external consumers
// (the website demo, and later Studio). Decoupled from the app entry
// (main.tsx) so embedders don't pull in the Vite-only globals or
// page-level imports.

export {
  InfiniteBoard,
  type InfiniteBoardHandle,
} from "./board/InfiniteBoard.js";
// Shared board↔nav hover channel — hosts create one store and hand it to
// both the board canvas and the nav pane's NavSource.
export { createHoverStore, type HoverStore } from "./board/hoverStore.js";
export {
  BoardCanvas,
  type BoardCanvasHandle,
  type BoardCanvasProps,
} from "./board/BoardCanvas.js";
export { BreadcrumbHud } from "./board/hud/BreadcrumbHud.js";
// Live-traffic overlay feed: hosts (Studio) create a store, mount the provider,
// and push per-service-pair rates; the board's particle layer animates them.
export {
  createLiveTrafficStore,
  LiveTrafficProvider,
  trafficEdgeKey,
  normServiceName,
  type LiveTrafficStore,
  type EdgeRates,
} from "./board/liveTrafficStore.js";
// Absolute req/sec → dots/sec compressor. Hosts (Studio's live-trace hook) map
// each edge's real rate through this so the overlay density reflects traffic
// honestly at low load and stays legible at 10k+ rpm.
export { flowSpawnRate, FLOW_RATE_KNEE } from "./board/particleFlow.js";
// The request-flow particle overlay, as a board-layer contribution component.
// The board no longer mounts it — a plugin (Studio's OTLP telemetry) contributes
// it through the workbench plugin registry; it reads live rates from a
// LiveTrafficProvider the plugin mounts.
export { ParticleFlowLayer } from "./board/ParticleFlowLayer.js";
// The "Live" header toggle for the request-flow animation (off by default) + its
// visibility store. The OTLP plugin contributes the toggle as a header item; the
// flow layer reads the store and unmounts when off.
export { LiveToggle } from "./board/telemetry/LiveToggle.js";
export {
  useFlowVisible,
  setFlowVisible,
  toggleFlowVisible,
  getFlowVisible,
  _resetFlowVisible,
} from "./board/telemetry/flowVisibilityStore.js";
// Live-stats feed (per-edge + per-node RED) + the details-panel telemetry
// sections that read it. A plugin (Studio's OTLP telemetry) mounts the provider,
// pushes a snapshot, and contributes the sections; absent provider → sections
// self-hide. Sibling to the lean per-edge rate store the overlay uses.
export {
  createLiveStatsStore,
  LiveStatsProvider,
  useLiveStatsStore,
  useEdgeStat,
  useNodeStat,
  type LiveStatsStore,
  type LiveStats,
  type EdgeStat,
  type NodeStat,
  type LatencyStat,
} from "./board/liveStatsStore.js";
export {
  EdgeTelemetrySection,
  ModuleTelemetrySection,
} from "./board/telemetry/TelemetrySections.js";
// Edge-health board overlay (red erroring edges, dim unobserved) — a plugin
// board-layer contribution reading the live-stats store.
export { TelemetryEdgeLayer } from "./board/telemetry/TelemetryEdgeLayer.js";
// Conformance-edge overlay — draws observed-but-undocumented edges (drift) as
// amber violation arcs between the two node rects. Reads live stats + node
// geometry (the board-layer contribution's `nodes` map).
export { ConformanceEdgeLayer } from "./board/telemetry/ConformanceEdgeLayer.js";
// Cross-space undocumented edges — folded into the board build as `violation`
// edges. The publisher (a headless board layer) derives the drift topology from
// live stats + the model and publishes it; the board solver reads it.
export { DriftTopologyPublisher } from "./board/telemetry/DriftTopologyPublisher.js";
export {
  setDriftTopology,
  useDriftTopology,
  _resetDriftTopology,
} from "./board/driftTopologyStore.js";
export {
  undocumentedObserved,
  modeledKeySet,
  driftTopology,
  driftTopologyKey,
  type UndocumentedEdge,
  type DriftPair,
} from "./board/telemetry/conformanceEdges.js";
// Live process-conformance (the "reordered" bucket): observed step orderings
// feed + the pure matcher that flags out-of-order steps against the model.
export {
  createLiveFlowsStore,
  LiveFlowsProvider,
  useLiveFlowsStore,
  useLiveFlows,
  type LiveFlowsStore,
} from "./board/telemetry/liveFlowsStore.js";
export {
  modeledStepSeq,
  analyzeReorder,
  type ModeledStep,
  type ObservedOrdering,
  type ReorderResult,
  type StepLike,
} from "./board/telemetry/processConformance.js";
export {
  edgeHealthStyle,
  EDGE_ERROR_THRESHOLD,
  type EdgeHealthStyle,
} from "./board/telemetry/edgeStyle.js";
export { useLiveEdgeStats } from "./board/liveStatsStore.js";
// Live-history ring buffer + sparkline — trend behind each stat.
export {
  createLiveHistoryStore,
  LiveHistoryProvider,
  useLiveHistoryStore,
  useEdgeHistory,
  useNodeHistory,
  HISTORY_MAX,
  type LiveHistoryStore,
  type HistoryPoint,
  type HistoryMap,
} from "./board/liveHistoryStore.js";
export { Sparkline, sparklinePoints } from "./board/telemetry/Sparkline.js";
// Rich visx trend charts the details-panel telemetry sections render — a metric
// area chart (rate/errors) + a latency p50–p95 band chart. Reusable elsewhere.
export {
  MetricAreaChart,
  LatencyBandChart,
  ChartCanvas,
  CHART_COLORS,
  niceTop,
  type MetricAreaChartProps,
  type LatencyBandChartProps,
  type LatencySample,
} from "./board/telemetry/charts/index.js";
// Deep-links from a selected element to the backend trace UI.
export {
  buildTraceLinks,
  jaegerSearchUrl,
  TraceLinkProvider,
  useTraceLinks,
  type TraceLinks,
  type TraceBackendKind,
} from "./board/telemetry/traceLinks.js";
// SLO health — compare live RED to per-service `slo.*` targets → ok/warn/breach.
// Pure; the coloring layer (and section badges) read it.
export {
  readSlo,
  evaluateHealth,
  type SloTarget,
  type HealthLevel,
  type HealthVerdict,
  type ObservedHealth,
} from "./board/telemetry/health.js";
// Model↔telemetry conformance — confirmed / undocumented / unobserved edges.
export {
  computeConformance,
  type EdgeConformance,
  type ConformanceResult,
  type ConformanceEdge,
  type EndpointPair,
} from "./board/telemetry/conformance.js";
// Workbench plugin host + contracts — re-exported here (the app-shell public
// barrel) so embedders can mount plugins without a second entry point. The
// plugin module pulls no provider-chain deps, so it's safe alongside the board
// exports. (Also available from `@archlang/app-shell/workbench`.)
export {
  WorkbenchPluginHost,
  PluginBoundary,
  isPluginCompatible,
  compatiblePlugins,
  WORKBENCH_PLUGIN_API_VERSION,
  useBoardLayerContributions,
  useHeaderItemContributions,
  resolveDetailSections,
  WorkbenchHostProvider,
  useWorkbenchHost,
  makeHostContext,
  NOOP_HOST_CONTEXT,
  isActionable,
  runActionable,
  useCommandContributions,
  useContextMenuContributions,
  CommandPaletteHost,
  HotkeyRegistryProvider,
  useHotkey,
  CommandSourceProvider,
  useRegisterCommandSource,
  RegistryBridge,
  publishDynamicMenuItems,
  useOutOfHostMenuItems,
  useOutOfHostSettingsPages,
  useOutOfHostCtx,
  type WorkbenchPlugin,
  type PluginContributions,
  type BoardLayerContribution,
  type DetailSectionContribution,
  type HeaderItemContribution,
  type CommandContribution,
  type MenuItemContribution,
  type ContextMenuContribution,
  type ContextMenuTarget,
  type MenuItemSpec,
  type SettingsPageContribution,
  type SettingsScope,
  type ResolvedMenuItem,
  type ResolvedSettingsPage,
  type PaletteCommand,
  type DetailSelection,
  type WorkbenchHostContext,
  type HostCapabilities,
  type Actionable,
  type RepoRef,
  type TabRef,
  type OpenTabSpec,
  type NavRef,
  type ToastSpec,
} from "./workbench/plugins/index.js";
export { walkFocal, type Viewport } from "@archlang/scene/board";
export {
  buildBoardFromModules,
  createBoardEngine,
  type PreviousLayout,
} from "@archlang/scene/board";
export type {
  BoardSpace,
  BoardItem,
  BoardEdge,
  PlacementOverrides,
} from "@archlang/scene/board";
export { flattenModulesWithParent } from "@archlang/scene/arch-model";
export {
  setWidgetsCss,
  registerBuiltinWidgets,
} from "./board/builtinWidgets.js";

// Side-effect on import: register the built-in `<arch-module>` /
// `<arch-template>` custom elements + every stdlib widget
// (arch-microservice, arch-database, archui-card, …). Without these,
// board nodes render empty containers and the diagram looks blank.
// Idempotent — safe to call from multiple consumers.
import { registerBuiltinWidgets as _register } from "./board/builtinWidgets.js";
import { installWidgetDefineGuard } from "./board/widgetGuard.js";
if (typeof customElements !== "undefined") {
  // Arm the define guard first so a duplicate/conflicting widget tag
  // from any bundle below degrades to a warning instead of crashing.
  installWidgetDefineGuard();
  _register();
  // Load the generated bundle that runs every stdlib package's
  // widgets.js side-effect (each calls customElements.define()).
  // Dynamic import so SSR doesn't choke on HTMLElement at module top.
  void import("./generated/widget-entrypoints.js");
}
export type {
  StableId,
  ArchitectureModel,
  DependencyEdge,
  Diagnostic,
} from "@archlang/scene/arch-model";

// Full-featured drop-in viewer (model pipeline + stress animation +
// drag/select). Consumers that just want a diagram from a Vfs reach
// for this; embedders that need lower-level control still have
// InfiniteBoard / buildBoardFromModules above.
export {
  ArchitectureView,
  type ArchitectureViewProps,
} from "./architecture-view/ArchitectureView.js";
// The module/edge inspector as a standalone, composable component — pair it
// with the board through a shared selection, place it anywhere, or omit it.
export { DetailsPanel, type DetailsPanelProps } from "./architecture-view/DetailsPanel.js";

// Sidebar panels — reused by Studio's git-attributed details panel.
export {
  ModuleDetails,
  EdgeDetails,
  type SelectionRef,
  type ConnRef,
  type Indexes,
  type BoardIndexes,
} from "./components/Sidebar.js";
export { selectionLabelForRef } from "./components/selectionLabel.js";

// The unified left-rail nav (files / types / processes / diff) — one
// component over a NavSource adapter, mounted by the viewer's
// WorkspacePanel, the workbench editor, Studio, and the diff boards.
export {
  WorkspaceNav,
  type WorkspaceNavProps,
  type NavView,
  type HostNavTab,
} from "./workbench/primitives/WorkspaceNav.js";
export type { NavSource, NavFile, NavModel, NavSelection } from "./workbench/nav/NavSource.js";
// Diff convenience wrapper — the unified WorkspaceNav in pure diff mode, fed
// by a ChangeSet. Both the viewer and Studio diff boards mount this.
export { DiffNav, type DiffNavProps } from "./workbench/nav/DiffNav.js";
// Shared diff/review right rail — before→after detail for the selected module.
// Both the viewer and Studio diff boards mount this.
export {
  DiffDetailsPanel,
  type DiffDetailsPanelProps,
  type DetailsModule,
} from "./components/diff/DiffDetailsPanel.js";
export { useModel, type ModelApi } from "./hooks/useModel.js";
export type { Mode } from "./hooks/useMode.js";

// Generic node-coloring layer — one mechanism (value→color + legend +
// per-node map) shared by label/aspect coloring and git-diff coloring,
// rendered as node stroke + minimap fill + legend. Adapters build a
// NodeColoring; NodeColoringProvider feeds the board.
// Lifecycle paint + the timeline scrubber ride the same paint-only channel.
export {
  NodeColoringProvider,
  useNodeColoring,
  buildAspectColoring,
  buildDiffColoring,
  ColorLegend,
  type NodeColoring,
  type ColorLegendEntry,
  type ColorLegendProps,
} from "./board/coloring/index.js";

export {
  LifecycleProvider,
  LifecycleScrubber,
  useLifecyclePaint,
  resolveLifecyclePaint,
  resolveLifecycleDelta,
  setScrubPoint,
  setComparePoint,
  resetScrub,
  useScrubPoint,
  useComparePoint,
  scrubStops,
  markerAt,
  type LifecyclePaintMap,
  type LifecycleDelta,
  type ScrubSelection,
} from "./board/lifecycle/index.js";

// Git-diff lookup context — shared so embedders (Studio) drive the SAME
// React context the board reads (ghost-links / details / edge status).
export { DiffProvider } from "./diff/DiffContext.js";
export { useDiffLookup } from "./diff/useDiffLookup.js";
// The diff render substrate — THE split/overlay boards (shared viewport store,
// hover mirror, single minimap, ghost links). Studio's git-diff pages mount
// these directly; keeping a per-app copy is how the two forks drifted apart
// (fixed-in-one-place minimap/viewport bugs stayed broken in the other).
export { SplitDiffBoard, type SplitDiffBoardProps } from "./diff/SplitDiffBoard.js";
export { OverlayDiffBoard, type OverlayDiffBoardProps } from "./diff/OverlayDiffBoard.js";

// Entity viewers — the type library + process swimlane the full app
// renders, packaged for embedders (Studio's editor preview shows these
// when a type/process is picked in the nav). TypesMode is presentational
// (model + selected name); ProcessViewer bundles the walker state.
export { TypesMode } from "./components/TypesMode.js";
export { ProcessViewer, type ProcessViewerProps } from "./process/ProcessViewer.js";

export { moduleKey } from "@archlang/scene/arch-model";
export type {
  Module,
  Process,
  TypeDef,
  Interface,
} from "@archlang/scene/arch-model";
